import React, { useState, useEffect } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Container,
  Paper,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Rating,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  AutoFixHigh as OptimizeIcon,
  Refresh as RefreshIcon,
  Publish as DeployIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Info as InfoIcon,
  TrendingUp as ScoreIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import BusinessProfilePreview from './BusinessProfilePreview';
import OptimizationTable from './OptimizationTable';

const OptimizationRecommendationsV2 = () => {
  const { locationId } = useParams();
  const { canUsePremiumFeatures, user } = useAuth();
  const { businesses, selectedBusiness, setSelectedBusiness, selectBusinessFromUrl } = useBusiness();
  
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState([]);
  const [deploymentStatus, setDeploymentStatus] = useState({});
  const [isLocked, setIsLocked] = useState(false);
  const [gbpScore, setGbpScore] = useState(0);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [pendingChangesCount, setPendingChangesCount] = useState(0);
  const [reviewsData, setReviewsData] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);

  useEffect(() => {
    if (locationId) {
      // Auto-select business based on URL
      selectBusinessFromUrl(window.location.pathname);
      
      loadBusinessData();
      loadPendingChangesCount();
      loadReviewsData();
    }
  }, [locationId]);

  // 🚀 UPDATED: React to business selection changes from dropdown
  useEffect(() => {
    if (selectedBusiness && locationId) {
      const businessLocationId = selectedBusiness.locationId || selectedBusiness.id;
      
      // Business context updated - URL should have been updated by BusinessSearchContext navigation
      if (businessLocationId === locationId) {
        console.log('🔄 Business context updated in Optimization, ensuring data is fresh:', {
          businessName: selectedBusiness.businessName,
          locationId: businessLocationId
        });
        
        // Data will be reloaded via locationId useEffect when URL changes
        // This is a safety check for business context synchronization
      }
    }
  }, [selectedBusiness, locationId]);

  // Load pending changes count
  const loadPendingChangesCount = async () => {
    if (locationId) {
      try {
        const response = await fetch(`/api/pending-changes/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        const data = await response.json();
        if (data.success && data.hasPendingChanges) {
          setPendingChangesCount(data.changes.length);
        } else {
          setPendingChangesCount(0);
        }
      } catch (error) {
        console.error('Error loading pending changes count:', error);
        setPendingChangesCount(0);
      }
    }
  };

  useEffect(() => {
    // Auto-select business if it matches the locationId
    if (businesses.length > 0 && locationId && !selectedBusiness) {
      const matchingBusiness = businesses.find(b => b.locationId === locationId || b.id === locationId);
      if (matchingBusiness) {
        setSelectedBusiness(matchingBusiness);
      }
    }
  }, [businesses, locationId, selectedBusiness, setSelectedBusiness]);

  const loadBusinessData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔍 Loading comprehensive business data for locationId: ${locationId}`);
      
      // Backend will resolve correct user ID from auth token (team owner for SubUsers)
      console.log('📋 Using authentication token for user resolution');
      
      // Try the new working endpoint first
      let response;
      try {
        console.log(`🔥 Trying force-working endpoint for real Google data...`);
        response = await fetch(`/api/force-working/${locationId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        
        if (response.ok) {
          console.log(`✅ Force-working endpoint successful!`);
        } else {
          throw new Error(`Force-working endpoint failed: ${response.status}`);
        }
      } catch (forceError) {
        console.warn(`⚠️ Force-working endpoint failed, falling back to comprehensive: ${forceError.message}`);
        
        // Fallback to original comprehensive endpoint
        response = await fetch(`/api/google-business/comprehensive/${locationId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📋 Received comprehensive business data:', result);
      
      if (result.success && result.data) {
        setBusinessData(result.data.attributes);
        
        // Set stats if available
        const stats = result.included?.find(item => item.type === 'location_stats');
        if (stats) {
          setGbpScore(stats.attributes.gbp_score || 0);
          setPendingTasksCount(stats.attributes.pending_tasks_count || 0);
        }
        
        // Load existing optimization suggestions
        await loadOptimizationSuggestions();
      } else {
        throw new Error(result.message || 'Unexpected response format');
      }

    } catch (error) {
      console.error('❌ Error loading comprehensive business data:', error);
      
      // Check if this is a token expiration error
      if (error.message.includes('401') || error.message.includes('Unauthorized') || error.message.includes('500')) {
        console.log('🔑 Google API error - using cached data');
        setError('Google-Authentifizierung möglicherweise abgelaufen. Die Daten werden aus dem Cache geladen.');
      }
      
      // Fallback: Try to use selectedBusiness from context
      if (selectedBusiness) {
        console.log('🔄 Using selectedBusiness as fallback data source');
        
        // Transform selectedBusiness to comprehensive format
        const fallbackData = {
          business_name: selectedBusiness.businessName || selectedBusiness.locationName || 'Unbekanntes Unternehmen',
          business_description: selectedBusiness.description || selectedBusiness.businessDescription || '',
          business_address: selectedBusiness.address?.formattedAddress || selectedBusiness.businessAddress || '',
          phone_numbers: selectedBusiness.phoneNumber ? [selectedBusiness.phoneNumber] : (selectedBusiness.phoneNumbers || []),
          website_uri: selectedBusiness.websiteUrl || selectedBusiness.website_uri || '',
          categories: Array.isArray(selectedBusiness.categories) ? selectedBusiness.categories : [],
          open_hours: selectedBusiness.businessHours || selectedBusiness.openingHours || {},
          attributes: selectedBusiness.attributes || {},
          services: selectedBusiness.services || [],
          special_hours: selectedBusiness.special_hours || [],
          latlng: selectedBusiness.latlng || selectedBusiness.coordinates || null,
          media_items: selectedBusiness.media_items || [],
          media_profile: selectedBusiness.media_profile || null,
          media_cover: selectedBusiness.media_cover || null,
          is_verified: selectedBusiness.is_verified || false,
          timezone: 'Europe/Berlin',
          reviews: selectedBusiness.reviews || { count: 0, avg_rating: 0 },
          questions: selectedBusiness.questions || { count: 0 }
        };
        
        setBusinessData(fallbackData);
        console.log('✅ Using fallback business data:', fallbackData);
        
        // Try to load optimization suggestions anyway
        try {
          await loadOptimizationSuggestions();
        } catch (suggError) {
          console.warn('Could not load optimization suggestions:', suggError.message);
        }
      } else {
        setError('Fehler beim Laden der Geschäftsdaten: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadOptimizationSuggestions = async () => {
    try {
      const response = await fetch(`/api/businesses/${locationId}/recommendations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.recommendations) {
          // Transform recommendations to optimization suggestions
          const suggestions = result.recommendations.map(rec => ({
            field: rec.type,
            title: rec.title,
            description: rec.description,
            suggestedValue: rec.suggestedValue,
            priority: rec.priority,
            impact: rec.impact
          }));
          setOptimizationSuggestions(suggestions);
          console.log(`📋 Loaded ${suggestions.length} optimization suggestions`);
        }
      }
    } catch (error) {
      console.warn('Could not load optimization suggestions:', error.message);
    }
  };

  const loadReviewsData = async () => {
    try {
      setReviewsLoading(true);
      setReviewsError(null);
      
      const response = await fetch(`/api/reviews/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setReviewsData(data);
      console.log('✅ Reviews data loaded:', data);
      
    } catch (error) {
      console.error('❌ Error loading reviews:', error);
      setReviewsError(error.message);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadBusinessData();
      await loadReviewsData();
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeployAll = async () => {
    if (!canUsePremiumFeatures()) {
      setError('Premium-Upgrade erforderlich für die Deployment-Funktion');
      return;
    }
    
    // Implementation would deploy all pending optimizations
    console.log('🚀 Deploying all optimizations...');
    // This would iterate through all pending optimizations and apply them
  };

  const handleFieldUpdate = async (fieldId, newValue) => {
    console.log(`🔄 Saving field ${fieldId} as pending change with value:`, newValue);
    
    // Mark field as pending deployment
    setDeploymentStatus(prev => ({
      ...prev,
      [fieldId]: 'pending'
    }));

    try {
      // Use the new pending changes system instead of direct Google API calls
      const currentValue = businessData?.[fieldId] || '';
      
      const response = await fetch(`/api/pending-changes/${locationId}/${fieldId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          value: newValue,
          oldValue: currentValue
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`✅ Successfully saved ${fieldId} as pending change:`, result);

      // Update pending changes count
      if (result.pendingChangesCount !== undefined) {
        setPendingChangesCount(result.pendingChangesCount);
      }

      // Mark as pending (not deployed until published)
      setDeploymentStatus(prev => ({
        ...prev,
        [fieldId]: 'pending'
      }));

      // Update local business data preview
      setBusinessData(prev => ({
        ...prev,
        [fieldId]: newValue
      }));

    } catch (error) {
      console.error(`❌ Error saving ${fieldId} as pending change:`, error);
      
      // Mark as error
      setDeploymentStatus(prev => ({
        ...prev,
        [fieldId]: 'error'
      }));

      // Show error to user
      setError(`Fehler beim Speichern von ${fieldId}: ${error.message}`);
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setError(null);
        setDeploymentStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[fieldId];
          return newStatus;
        });
      }, 5000);

      // Re-throw error to keep edit dialog open
      throw error;
    }
  };

  const handleBusinessChange = (event) => {
    const businessId = event.target.value;
    const business = businesses.find(b => b.id === businessId || b.locationId === businessId);
    if (business) {
      setSelectedBusiness(business);
      // Navigate to the new business
      window.location.href = `/optimization/${business.locationId || business.id}`;
    }
  };

  const calculateOptimizationScore = () => {
    if (!businessData) return 0;
    
    // Calculate based on completeness of key fields
    const fields = [
      businessData.business_name,
      businessData.business_description,
      businessData.business_address,
      businessData.phone_numbers?.[0],
      businessData.website_uri,
      businessData.categories?.[0],
      Object.keys(businessData.open_hours || {}).length > 0,
      Object.keys(businessData.attributes || {}).length > 0
    ];
    
    const completedFields = fields.filter(Boolean).length;
    return Math.round((completedFields / fields.length) * 100);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#C41E3A', color: amDesignColors.white, borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <OptimizeIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ color: amDesignColors.white }}>
                  Google Business Profile Optimierung
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, color: amDesignColors.white }}>
                  Verbessern Sie Ihr Google Business Profile für bessere lokale Rankings
                </Typography>
              </Box>
            </Box>
            
            {/* Business Selector - Fixed dropdown clipping */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 300, bgcolor: amDesignColors.white, borderRadius: 1 }}>
                <InputLabel sx={{ fontSize: '0.875rem' }}>Unternehmen auswählen</InputLabel>
                <Select
                  value={selectedBusiness?.locationId || selectedBusiness?.id || ''}
                  onChange={handleBusinessChange}
                  label="Unternehmen auswählen"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word'
                        }
                      }
                    }
                  }}
                >
                  {businesses.map((business) => (
                    <MenuItem 
                      key={business.locationId || business.id} 
                      value={business.locationId || business.id}
                      sx={{ minHeight: 48 }}
                    >
                      {business.businessName || business.locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* AI Suggestions Counter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={<OptimizeIcon />}
                label={`${optimizationSuggestions.length} A&M Empfehlungen`}
                sx={{ bgcolor: amDesignColors.white, color: '#C41E3A', fontWeight: 'bold' }}
              />
              <Chip
                icon={<ScoreIcon />}
                label={`${calculateOptimizationScore()}% Vollständigkeit`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: amDesignColors.white }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
              {/* Main Action Buttons */}
              <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{ 
                    color: amDesignColors.white, 
                    borderColor: amDesignColors.white, 
                    '&:hover': { borderColor: amDesignColors.white, bgcolor: 'rgba(255,255,255,0.1)' } 
                  }}
                >
                  {refreshing ? 'Aktualisiere...' : 'Aktualisieren'}
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<DeployIcon />}
                  onClick={handleDeployAll}
                  sx={{ 
                    bgcolor: amDesignColors.success, 
                    color: amDesignColors.white,
                    '&:hover': { bgcolor: '#45A049' }
                  }}
                >
                  Alle Änderungen übernehmen
                </Button>
              </Stack>
              
              {/* Reset Button */}
              <Button
                variant="outlined"
                onClick={async () => {
                  if (window.confirm('Alle nicht veröffentlichten Änderungen zurücksetzen?')) {
                    try {
                      // Delete all pending changes from server
                      const response = await fetch(`/api/pending-changes/${locationId}/reset`, {
                        method: 'DELETE',
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        }
                      });
                      
                      const result = await response.json();
                      
                      if (result.success) {
                        console.log('✅ All pending changes reset successfully');
                        // IMMEDIATELY set counter to 0
                        setPendingChangesCount(0);
                        // Reset local state
                        setDeploymentStatus({});
                        // Force reload of business data
                        await loadBusinessData();
                        // Force reload pending changes count
                        await loadPendingChangesCount();
                        // Force trigger pending changes count update in child components
                        window.dispatchEvent(new CustomEvent('pendingChangesReset'));
                        // Also call the OptimizationTable reset function if available
                        if (window.resetPendingChanges) {
                          await window.resetPendingChanges();
                        }
                      } else {
                        console.error('❌ Failed to reset pending changes:', result.error);
                        setError('Fehler beim Zurücksetzen der Änderungen: ' + result.error);
                      }
                    } catch (error) {
                      console.error('❌ Error resetting changes:', error);
                      setError('Fehler beim Zurücksetzen der Änderungen: ' + error.message);
                    }
                  }
                }}
                sx={{ 
                  color: amDesignColors.white, 
                  borderColor: amDesignColors.white, 
                  fontSize: '0.8rem',
                  py: 0.5,
                  px: 2,
                  '&:hover': { borderColor: amDesignColors.white, bgcolor: 'rgba(255,255,255,0.1)' } 
                }}
              >
                Änderungen zurücksetzen
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Content - Two Panel Layout */}
      <Grid container spacing={3}>
        {/* Left Panel - Optimization Table */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid amDesignColors.border' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon />
                  Geschäftsprofil Übersicht
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Alle wichtigen Geschäftsinformationen und deren Vollständigkeit
                </Typography>
              </Box>
              
              <OptimizationTable
                businessData={businessData}
                reviewsData={reviewsData}
                onFieldUpdate={handleFieldUpdate}
                optimizationSuggestions={optimizationSuggestions}
                locationId={locationId}
                pendingChangesCountProp={pendingChangesCount}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel - Business Profile Preview */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'sticky', top: 20 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon />
                Google Vorschau
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                So erscheint Ihr Unternehmen in der Google Suche
              </Typography>
              
              <BusinessProfilePreview businessData={businessData} reviewsData={reviewsData} compact={true} />
              
              {/* Quick Stats */}
              {businessData && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Schnellstatistiken
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Bewertungen:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {reviewsLoading ? '...' : (reviewsData?.statistics?.totalReviews || businessData.reviews?.count || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Durchschnitt:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {reviewsLoading ? '...' : (reviewsData?.statistics?.averageRating?.toFixed(1) || businessData.reviews?.avg_rating || 0)} ⭐
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Bilder:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {businessData.media_items?.length || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Antwortrate:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {reviewsLoading ? '...' : (reviewsData?.statistics?.responseRate ? `${reviewsData.statistics.responseRate}%` : 'N/A')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Social Media:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {Object.keys(businessData.attributes || {}).filter(key => key.includes('url')).length}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default OptimizationRecommendationsV2;